import { useEffect, useMemo, useState } from "react";
import { LockBadge } from "../../components/LockBadge";
import {
  getInstalledApps, getBlockedPackages, setBlockedPackages,
  isUsageAccessEnabled, canDrawOverlays,
  openUsageAccessSettings, requestOverlayPermission,
  getBlockingState,
} from "../../capacitor/appBlocker";
import Modal from "../../components/Modal";
import { usePremium } from "../../firebase/billing";
import { OPTION_SHADOW } from "../../theme/sharedStyles";

const btnStyle = {
  width: "100%", padding: "11px 0", borderRadius: 14,
  border: "none", boxShadow: OPTION_SHADOW, background: "var(--c-bg-card)",
  color: "var(--c-text-accent)", fontFamily: "inherit", fontSize: 13,
  fontWeight: 700, cursor: "pointer",
};

// App Blocker settings content, nested under SettingsPanel's own collapsible
// "App Blocker" row. Blocking only ever happens automatically during
// Clock-In: whatever's checked below gets covered the moment the
// timer/stopwatch is started, and stays covered through a pause — the only
// way out mid-session is revoking Usage Access in Android Settings
// (intentional friction, not a bug). There used to also be a standalone
// "Block apps now" toggle for blocking on demand any time — removed
// deliberately, since it gave this app blocker-app-style behavior/scope that
// Clarity (a multi-function productivity app, not a dedicated blocker)
// doesn't want, and it made the friction above pointless anyway.
//
// Because the only intended exit is revoking Usage Access, the picker
// itself locks (checkboxes disabled) for as long as a Clock-In session is
// active — otherwise unchecking an app here would silently unblock it
// mid-session, defeating the point.
// A "prominent disclosure" shown inside the app before the user is sent to
// Android's Usage access settings: it plainly states what the permission is
// used for, with two clearly labelled choices (Google's earlier rejection of the
// Accessibility version was partly for a single-button disclosure). Backing out
// or tapping away is never treated as consent.
const DISCLOSURE_TEXT = "Clarity uses Usage access only to see which app is open during a Clock-In session, so it can cover the apps you chose to block. It doesn't read what's on your screen, and nothing about your app usage leaves your phone.";

export default function AppBlockerSection() {
  // App Blocker is Premium, decided by the account alone. There used to be a
  // "grandfather" rule here (anyone with a blocklist already saved got it
  // free), but that blocklist lives on the phone, not the account, so any free
  // account on a phone that had ever saved one got the whole feature — and the
  // 2.5s failsafe below flipped the locked label into the full list once the
  // slow app-icon load finished.
  const { isPremium, loading: premiumLoading } = usePremium();
  const [usageAccessOn, setUsageAccessOn] = useState(false);
  const [overlayOn, setOverlayOn] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [apps, setApps] = useState(null); // null = still loading
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [sessionActive, setSessionActive] = useState(false);
  // Stays false only until the very first permission/session check resolves —
  // before that, usageAccessOn/overlayOn/sessionActive are all just their
  // useState defaults, indistinguishable from "actually denied"/"no session".
  // Without this, opening Settings while a session is active could briefly
  // flash the "grant these two permissions" prompt (real report: 1-2s) before
  // the async native-plugin calls resolved and swapped in the correct locked-
  // session message. Batching all three checks with Promise.all (instead of
  // three independent .then()s) also means they now always update together,
  // not staggered across separate render passes.
  const [permsReady, setPermsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const refreshPerms = () => {
      // allSettled, not all: these are three independent native calls, and one
      // of them being slow or never coming back must not hold the other two
      // hostage. With Promise.all it did — a single unresolved bridge call left
      // permsReady false, and the `return null` below turned that into a
      // completely blank App Blocker section rather than a partly-filled one.
      Promise.allSettled([isUsageAccessEnabled(), canDrawOverlays(), getBlockingState()])
        .then(([usage, overlay, state]) => {
          if (cancelled) return;
          if (usage.status === "fulfilled") setUsageAccessOn(usage.value);
          if (overlay.status === "fulfilled") setOverlayOn(overlay.value);
          if (state.status === "fulfilled") setSessionActive(state.value.sessionActive);
          setPermsReady(true);
        });
    };
    refreshPerms();
    // Last resort: show the section even if a native call never settles at all.
    // The flicker permsReady exists to prevent is worth about a second of
    // waiting — never a permanently empty panel.
    const failsafe = setTimeout(() => { if (!cancelled) setPermsReady(true); }, 2500);
    document.addEventListener("visibilitychange", refreshPerms); // pick up permission/session changes made elsewhere
    return () => { cancelled = true; clearTimeout(failsafe); document.removeEventListener("visibilitychange", refreshPerms); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getInstalledApps(), getBlockedPackages(), getBlockingState()])
      .then(([installedApps, blocked, state]) => {
        if (cancelled) return;
        setApps(installedApps);
        setSelected(new Set(blocked));
        setSessionActive(state.sessionActive);
      })
      .catch(() => { if (!cancelled) setApps([]); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!apps) return [];
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter(a => a.label.toLowerCase().includes(q));
  }, [apps, query]);

  const toggleApp = (packageName) => {
    if (sessionActive) return; // locked for the duration of the Clock-In session, see file header
    const next = new Set(selected);
    if (next.has(packageName)) next.delete(packageName); else next.add(packageName);
    setSelected(next);
    setBlockedPackages(Array.from(next)).catch(() => {});
  };

  const canBlock = usageAccessOn && overlayOn;

  // Nothing real to show yet — wait for the first check rather than flashing
  // a "not granted" prompt that may just be stale defaults. Three bouncing
  // dots + "Loading" fill the gap so the section doesn't look like an empty box
  // for the second or two it takes.
  if (!permsReady || premiumLoading) {
    return (
      <div role="status" aria-live="polite" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "20px 0" }}>
        <style>{`@keyframes abDotBounce { 0%, 80%, 100% { transform: translateY(0); opacity: 0.45; } 40% { transform: translateY(-7px); opacity: 1; } }`}</style>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 16 }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--c-primary)", animation: `abDotBounce 1.1s ease-in-out ${i * 0.15}s infinite` }} />
          ))}
        </div>
        <span style={{ fontSize: 12, color: "var(--c-text-soft)", fontWeight: 600 }}>Loading</span>
      </div>
    );
  }

  // Locked indicator for free accounts. Not tappable — the only path to the
  // Premium page is the Upgrade button on the Settings panel's own Premium
  // row. Exception: a session that is already blocking (armed while Premium)
  // still shows its "complete your session" note below rather than a lock.
  if (!isPremium && !sessionActive) {
    return (
      <div style={{ ...btnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "default", color: "var(--c-text-soft)" }}>
        <LockBadge size={18} /> App Blocker is a Premium feature
      </div>
    );
  }

  return (
    <div>
      {!usageAccessOn && (
        <button onClick={() => setShowDisclosure(true)} style={{ ...btnStyle, marginBottom: 8 }}>
          1. Turn on Usage access for Clarity
        </button>
      )}

      {showDisclosure && (
        <Modal onClose={() => setShowDisclosure(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--c-bg-card)", borderRadius: 18, padding: "20px 20px 16px",
            maxWidth: 340, boxShadow: "0 12px 30px rgba(0,0,0,0.28)",
          }}>
            <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.55, color: "var(--c-text)", fontWeight: 600 }}>
              {DISCLOSURE_TEXT}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowDisclosure(false)} style={btnStyle}>
                Not now
              </button>
              <button
                onClick={() => { setShowDisclosure(false); openUsageAccessSettings(); }}
                style={btnStyle}
              >
                Continue
              </button>
            </div>
          </div>
        </Modal>
      )}
      {!overlayOn && (
        <button onClick={() => requestOverlayPermission()} style={{ ...btnStyle, marginBottom: 8 }}>
          2. Turn on "Display over other apps" for Clarity
        </button>
      )}

      {canBlock && (
        <>
          {sessionActive ? (
            <p style={{
              margin: "0 0 10px", padding: "9px 12px", borderRadius: 12,
              background: "var(--c-bg-card)", border: "none", boxShadow: OPTION_SHADOW,
              fontSize: 12, color: "var(--c-text-accent)", fontWeight: 600, lineHeight: 1.5,
            }}>
              Complete your current session to unblock the apps & see the list.
            </p>
          ) : (
            <>
              <input
                type="text"
                placeholder="Search apps…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                style={{
                  width: "100%", boxSizing: "border-box", padding: "9px 12px",
                  borderRadius: 12, border: "none", boxShadow: OPTION_SHADOW,
                  fontFamily: "inherit", fontSize: 13, margin: "2px 0 10px",
                  background: "var(--c-bg-card)", color: "var(--c-text)",
                }}
              />
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                {apps === null && (
                  <div style={{ padding: "14px 0", fontSize: 12.5, color: "var(--c-text-soft)" }}>Loading installed apps…</div>
                )}
                {apps !== null && filtered.length === 0 && (
                  <div style={{ padding: "14px 0", fontSize: 12.5, color: "var(--c-text-soft)" }}>No apps match "{query}"</div>
                )}
                {filtered.map(app => (
                  <label
                    key={app.packageName}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "9px 0",
                      borderBottom: "1px solid var(--c-border)", cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" checked={selected.has(app.packageName)} onChange={() => toggleApp(app.packageName)} style={{ margin: 0 }} />
                    {app.icon && <img src={app.icon} alt="" width={24} height={24} style={{ borderRadius: 6, flexShrink: 0 }} />}
                    <span style={{ fontSize: 13, color: "var(--c-text)" }}>{app.label}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
